<script lang="ts">
    import { LL } from "../../../../i18n/i18n-svelte";
    import { noiseReductionStore } from "../../../Stores/NoiseReductionStore";
    import type { NoiseReductionProvider } from "../../../Stores/NoiseReductionStore";
    import SectionTitle from "./SectionTitle.svelte";

    function selectProvider(provider: NoiseReductionProvider): void {
        noiseReductionStore.setProvider(provider);
    }

    $: currentProvider = $noiseReductionStore.provider;
    $: effectiveProvider = $noiseReductionStore.effectiveProvider;
    $: status = $noiseReductionStore.status;
    $: fallbackReason = $noiseReductionStore.fallbackReason;
    $: isFallback = status === "fallback";
    $: isUnavailable = status === "unavailable";
    $: isLoading = status === "loading";
    $: isActiveProvider = (provider: NoiseReductionProvider) =>
        effectiveProvider === provider && (status === "active" || status === "fallback");
</script>

<div class="flex flex-col gap-1">
    <!-- svelte-ignore a11y-label-has-associated-control -->
    <SectionTitle title={$LL.camera.noiseReduction.title()} />

    <div class="flex flex-col gap-0.5 px-2">
        <!-- Off option -->
        <button
            class="flex items-center gap-2 py-1 px-1 rounded text-sm text-left w-full transition-colors
                {currentProvider === 'passthrough'
                ? 'bg-white/15 text-white'
                : 'text-white/70 hover:bg-white/10 hover:text-white'}"
            on:click={() => selectProvider("passthrough")}
            title={$LL.camera.noiseReduction.off()}
        >
            <span class="w-2 h-2 rounded-full flex-shrink-0 {currentProvider === 'passthrough' ? 'bg-blue-400' : 'bg-white/20'}"></span>
            <span>{$LL.camera.noiseReduction.off()}</span>
            {#if isActiveProvider("passthrough")}
                <span class="ml-auto text-xs text-green-400">{$LL.camera.noiseReduction.active()}</span>
            {/if}
        </button>

        <!-- Browser option -->
        <button
            class="flex items-center gap-2 py-1 px-1 rounded text-sm text-left w-full transition-colors
                {currentProvider === 'browser'
                ? 'bg-white/15 text-white'
                : 'text-white/70 hover:bg-white/10 hover:text-white'}"
            on:click={() => selectProvider("browser")}
            title={$LL.camera.noiseReduction.browser()}
        >
            <span class="w-2 h-2 rounded-full flex-shrink-0 {currentProvider === 'browser' ? 'bg-blue-400' : 'bg-white/20'}"></span>
            <span>{$LL.camera.noiseReduction.browser()}</span>
            {#if isActiveProvider("browser")}
                <span class="ml-auto text-xs text-green-400">{$LL.camera.noiseReduction.active()}</span>
            {/if}
        </button>

        <!-- RNNoise option -->
        <button
            class="flex items-center gap-2 py-1 px-1 rounded text-sm text-left w-full transition-colors
                {currentProvider === 'rnnoise'
                ? 'bg-white/15 text-white'
                : 'text-white/70 hover:bg-white/10 hover:text-white'}"
            on:click={() => selectProvider("rnnoise")}
            title={$LL.camera.noiseReduction.rnnoise()}
        >
            <span class="w-2 h-2 rounded-full flex-shrink-0 {currentProvider === 'rnnoise' ? 'bg-blue-400' : 'bg-white/20'}"></span>
            <span>{$LL.camera.noiseReduction.rnnoise()}</span>
            {#if currentProvider === "rnnoise" && isLoading}
                <span class="ml-auto text-xs text-white/50 italic">{$LL.camera.noiseReduction.loading()}</span>
            {:else if currentProvider === "rnnoise" && isActiveProvider("rnnoise")}
                <span class="ml-auto text-xs text-green-400">{$LL.camera.noiseReduction.active()}</span>
            {/if}
        </button>
    </div>

    {#if isFallback || (isUnavailable && currentProvider === "rnnoise" && fallbackReason)}
        <div class="mx-2 px-2 py-1 rounded bg-yellow-900/40 border border-yellow-600/40">
            {#if isFallback}
                <p class="text-xs text-yellow-300">{$LL.camera.noiseReduction.fallbackWarning()}</p>
            {/if}
            {#if fallbackReason}
                <p class="text-[11px] text-yellow-200/80 mt-1 break-words">{fallbackReason}</p>
            {/if}
        </div>
    {/if}
</div>
